<template v-slot:extension>
    <div>
        <v-card v-if="experiment">
            <v-toolbar-title class="pa-3">
                <v-row no-gutters>
                    <v-col cols="6">
                        <h5 class="pl-2 expInfo">
                            <a :href="experiment.url" target="_blank" style="text-decoration: none"
                                >EXPERIMENT</a
                            >
                            : {{ experimentCode }} - {{ experiment.name }}
                            {{ experiment.setupStatus === "Draft" ? "(DRAFT)" : "" }}
                        </h5>
                        <br />
                        <h5 class="pl-2 expInfo" v-if="experimentGroup">
                            EXPERIMENT GROUP : {{ experimentGroup.name }} (<router-link
                                style="text-decoration: none"
                                :to="{
                                    name: 'ExperimentGroupView',
                                    params: { code: experimentGroup.code },
                                }"
                            >
                                {{ experimentGroup.code }} </router-link
                            >)
                        </h5>
                    </v-col>
                    <v-col
                        cols="6"
                        align="end"
                        v-if="!hasConfluencePage && userCanCreateConfluencePages"
                    >
                        <v-btn
                            variant="elevated"
                            color="primary"
                            size="small"
                            @click="createConfluencePage"
                        >
                            Create Confluence Page
                        </v-btn>
                    </v-col>
                </v-row>
            </v-toolbar-title>
            <v-tabs bg-color="info" theme="dark">
                <v-tab
                    @click="setTab(tab.name)"
                    v-for="tab in visibleTabs"
                    :key="tab.name"
                    :text="tabName"
                    style="color: white"
                >
                    {{ tab.name }}
                </v-tab>
                <entity-menu
                    v-if="isEntitiesV1TabVisible()"
                    title="Entities"
                    :thing-schemas="thingSchemas"
                    @thingSchemaSelected="selectThingSchemaForV1"
                    @downloadCustomEntitySetupExcel="downloadCustomEntitySetupExcel"
                />
                <entity-menu
                    v-if="isEntitiesV2TabVisible()"
                    title="Entities"
                    :thing-schemas="thingSchemas"
                    @thingSchemaSelected="selectThingSchema"
                    @downloadCustomEntitySetupExcel="downloadV2CustomEntitySetupExcel"
                />
            </v-tabs>
        </v-card>
        <fermenter-plates
            v-if="tabName === 'Fermenter Plates'"
            :experiment-code="experiment.code"
        />
        <entity-list
            v-if="tabName === 'Entities'"
            :key="`list-${selectedThingSchema.id}`"
            :experiment="experiment"
            :protocol="protocol"
            :thingSchema="selectedThingSchema"
            :search="search"
            :is-using-measurements="isUsingMeasurements()"
        />
        <manage-things-tab
            v-if="tabName === 'Entities V2' && selectedThingSchema != null"
            :protocol="protocol || schemaConfig"
            :experiment="experiment"
            :thing-schema="selectedThingSchema"
            :task-schedule="taskSchedule"
            :search="search"
            @configureSamples="configureSamples"
        />
        <experiment-metadata-form
            v-if="tabName === 'Metadata'"
            :experiment="experiment"
            :show-downloads="true"
        />
        <microbial-tab
            v-if="tabName === 'Microbial'"
            :protocol="protocol"
            :experiment="experiment"
        />
        <experiment-data
            v-if="tabName === 'Data'"
            :experimentData="experimentData"
            :protocolId="protocolCode"
            :customEntityList="entityList"
            :search="search"
        />
        <experiment-tasks
            v-if="tabName === 'Tasks'"
            :protocol="protocol || schemaConfig"
            :experiment="experiment"
            :task-schedule="taskSchedule"
            @reloadTaskSchedule="reloadTaskSchedule"
        />
        <measurements-tab
            v-if="tabName === 'Measurements'"
            :protocol="protocol || schemaConfig"
            :experiment="experiment"
        />
        <samples-tab v-if="tabName === 'Samples'" :protocol="protocol" :experiment="experiment" />
        <photos-tab
            v-if="tabName === 'Photos'"
            :protocol="protocol"
            :experiment="experiment"
            :task-schedule="taskSchedule"
        />
        <files-tab v-if="tabName === 'Files'" :experimentCode="experimentCode" :search="search" />
        <samples-tab-new
            v-if="tabName === 'Samples v2'"
            :experimentCode="experimentCode"
            :configureSamplesContext="configureSamplesContext"
        />
        <experiment-setup-details-tab
            v-if="tabName === 'Setup'"
            :experiment="experiment"
            :schema-config="protocol || schemaConfig"
            :setup-status="experiment.setupStatus"
        />
        <experiment-config-records-tab
            v-if="tabName === 'Config Records'"
            :experiment="experiment"
        />
    </div>
</template>

<script>
import { defineComponent } from "vue";

import _ from "lodash";
import { legacyExperimentAPI, requestsAPI, taskScheduleAPI } from "@/services/api.services";
import { EventBus } from "@/lib/event-bus.js";
import EntityList from "./EntityList.vue";
import ExperimentData from "./ExperimentData.vue";
import { mapGetters } from "vuex";
import { withLoadingScreen } from "@/lib/loading.js";
import ExperimentTasks from "./TaskScheduleTab.vue";
import MeasurementsTab from "./MeasurementsTab.vue";
import SamplesTab from "./SamplesTab.vue";
import { DateTime } from "luxon";
import { toDateTimeForExperiment } from "@/lib/dateUtils.js";
import EntityMenu from "./EntityMenu.vue";
import ManageThingsTab from "./ManageThingsTab.vue";
import {
    downloadCustomEntitySetupExcel,
    downloadSummaryOneTimeMeasurementSetExcel,
} from "@/lib/thingUtils.ts";
import PhotosTab from "@/components/Experiments/PhotosTab.vue";
import FilesTab from "./FilesTab.vue";
import SamplesTabNew from "./SamplesTabNew.vue";
import MicrobialTab from "@/components/Experiments/MicrobialTab.vue";
import { thingAPI } from "@/services/things/thingAPI.ts";
import FermenterPlates from "@/components/FermenterRuns/FermenterPlates.vue";
import {
    fetchAllEntityDataOTForExperimentCode,
    fetchEntitySchemasByProtocolCode,
} from "@/lib/legacyThingUtils.js";
import ExperimentMetadataForm from "./ExperimentMetadataForm.vue";
import ExperimentSetupDetailsTab from "@/components/Experiments/ExperimentSetupDetailsTab.vue";
import ExperimentConfigRecordsTab from "@/components/ExperimentConfigRecords/ExperimentConfigRecordsTab.vue";
import { confluenceAPI } from "@/services/confluence/confluenceAPI.ts";

export default defineComponent({
    components: {
        ExperimentConfigRecordsTab,
        ExperimentSetupDetailsTab,
        FermenterPlates,
        MicrobialTab,
        PhotosTab,
        ManageThingsTab,
        EntityMenu,
        MeasurementsTab,
        EntityList,
        ExperimentData,
        ExperimentMetadataForm,
        ExperimentTasks,
        SamplesTab,
        SamplesTabNew,
        FilesTab,
    },

    name: "DataMain",

    props: {
        search: {
            type: String,
            default: undefined,
        },
    },

    data() {
        return {
            experimentCode: "",
            protocolCode: "",
            protocol: null, // the protocol object, not Benchling or DB record
            schemaConfig: null, // for Invaio experiments
            experimentGroup: null,
            thingSchemas: [],
            experimentData: null,
            taskSchedule: null,
            selectedThingSchema: null,
            tabName: "",
            expName: "",
            protocolName: "",
            entityList: [],
            tabs: [],
            plateView: false,
            isShowUserMeasurementsForTaskAndReadoutGroup: false,
            dataCollectionTaskForUserMeasurements: null,
            readoutGroupForUserMeasurements: null,
            experiment: null,
            requestsCount: 0,
            configureSamplesContext: null,
            hasConfluencePage: false,
        };
    },

    async mounted() {
        this.experimentCode = this.$route.params.code;

        EventBus.$on("loadExperimentData", this.loadExperimentData);
        await withLoadingScreen(async () => {
            try {
                await this.loadExperimentData();
                await this.getRequestsCount();
            } catch (err) {
                console.log("failed to load schedule", err);
                const msg = _.get(err, "response.data.error.message") || err.message;
                EventBus.$emit("globalAlert", `An error occurred: ${msg}.`);
            }

            this.setupTabs();

            if (this.isBenchlingExperiment && this.protocol.isMicrobial) {
                this.tabName = "Microbial";
            } else if (this.isUsingMeasurements()) {
                this.tabName = "Measurements";
            } else {
                this.tabName = "Data";
            }
        });
    },

    computed: {
        ...mapGetters(["isAuthorizedForOneOf"]),
        visibleTabs() {
            return this.tabs.filter((tab) => tab.visible());
        },
        isBenchlingExperiment() {
            return this.experiment.isBenchling;
        },
        userCanCreateConfluencePages() {
            return this.isAuthorizedForOneOf("Experiments:UseConfluenceSetup");
        },
    },

    methods: {
        isFermenterRunExperiment() {
            return this.isBenchlingExperiment && this.protocol.protocolType === "Fermenter Run";
        },
        isUsingMeasurements() {
            return this.experiment && this.experiment.isUsingMeasurements;
        },
        setupTabs() {
            this.tabs = [
                {
                    name: "Data",
                    visible: () => {
                        return this.isDataTabVisible();
                    },
                },
                {
                    name: "Microbial",
                    visible: () => {
                        return this.protocol && this.protocol.isMicrobial;
                    },
                },
                {
                    name: "Fermenter Plates",
                    visible: () => {
                        return this.isFermenterRunExperiment();
                    },
                },
                {
                    name: "Measurements",
                    visible: () => {
                        return this.isUsingMeasurements();
                    },
                },
                {
                    name: "Metadata",
                    visible: () => {
                        return true;
                    },
                },
                {
                    name: "Tasks",
                    visible: () => {
                        return (
                            this.isAuthorizedForOneOf("Tasks:Manage") && this.isUsingMeasurements()
                        );
                    },
                },
                {
                    name: "Samples",
                    visible: () => {
                        return (
                            this.isAuthorizedForOneOf("Samples:Manage") && this.requestsCount > 0
                        );
                    },
                },
                {
                    name: "Photos",
                    visible: () => {
                        return this.isAuthorizedForOneOf("Photos:Read", "Photos:Manage");
                    },
                },
                {
                    name: "Files",
                    visible: () => {
                        return true; // TODO: authZ
                    },
                },
                {
                    name: "Samples v2",
                    visible: () => {
                        return this.isAuthorizedForOneOf("MaterialSamples:Manage");
                    },
                },
                {
                    name: "Setup",
                    visible: () => {
                        return this.isAuthorizedForOneOf("ExperimentRelationships:Read");
                    },
                },
                {
                    name: "Config Records",
                    visible: () => {
                        return this.isAuthorizedForOneOf("Auth:Manage");
                    },
                },
            ];
        },
        isDataTabVisible() {
            return (
                this.isBenchlingExperiment &&
                !this.isUsingMeasurements() &&
                !this.protocol.isMicrobial
            );
        },
        isEntitiesV1TabVisible() {
            return this.hasEntities() && !this.isUsingMeasurements();
        },
        isEntitiesV2TabVisible() {
            return this.hasEntities() && this.isUsingMeasurements();
        },
        hasEntities() {
            return !this.plateView && this.thingSchemas.length > 0;
        },
        setTab(tab) {
            if (tab === "Data" && this.$route.params.date)
                this.$router.push({
                    path: `/experiment/${this.experimentCode}/${this.protocolCode}`,
                });
            this.tabName = tab;
            console.log("Setting Tab:", tab);
        },
        selectThingSchemaForV1(thingSchema) {
            this.setTab("Entities");
            this.selectedThingSchema = thingSchema;
        },
        selectThingSchema(thingSchema) {
            this.setTab("Entities V2");
            this.selectedThingSchema = thingSchema;
        },
        downloadCustomEntitySetupExcel(thingSchema) {
            downloadCustomEntitySetupExcel({
                protocolCode: this.protocolCode,
                experimentCode: this.experimentCode,
                entityName: thingSchema.name,
                entityParent: thingSchema.parentThingSchemaName,
            });
        },
        downloadV2CustomEntitySetupExcel(thingSchema) {
            downloadSummaryOneTimeMeasurementSetExcel({
                experimentCode: this.experimentCode,
                thingSchemaId: thingSchema.id,
            });
        },
        async loadExperimentData() {
            const { experiment, experimentGroup, schemaConfig, protocol } =
                await thingAPI.fetchExperimentByCode(this.experimentCode);
            this.experiment = {
                ...experiment,
                day0: experiment.day0
                    ? DateTime.fromISO(experiment.day0, { zone: "UTC" })
                    : DateTime.now({ zone: experiment.siteTimeZone }),
            };
            this.schemaConfig = schemaConfig;
            this.thingSchemas = schemaConfig.thingSchemas;
            this.experimentGroup = experimentGroup;
            this.protocol = protocol;
            this.reloadTaskSchedule();

            const confluenceNotebookEntryUrl =
                await confluenceAPI.fetchNotebookEntryUrlForExperimentCode(this.experimentCode);
            this.hasConfluencePage = confluenceNotebookEntryUrl !== null;

            if (!this.experiment.isUsingMeasurements) {
                await this.setupLegacyView();
            }
        },
        reloadTaskSchedule() {
            withLoadingScreen(async () => {
                this.taskSchedule = await taskScheduleAPI.fetchTaskScheduleForExperimentCode(
                    this.experiment.code
                );
            });
        },
        async setupLegacyView() {
            /// legacy after this
            this.experimentData = await legacyExperimentAPI.fetchExperimentByCode(
                this.experimentCode
            );

            // Fetch Mango experiment data
            const siteTimeZone = this.experimentData.expData.site_time_zone || "UTC";
            this.experimentData.mangoData = await fetchAllEntityDataOTForExperimentCode(
                this.experimentCode,
                siteTimeZone
            );

            const expData = this.experimentData.expData;
            // TODO: expData.file_registry_id$ is the protocol code, not the experiment code.
            this.protocolCode = expData.file_registry_id$;
            this.protocolName = expData.name;
            this.expName = expData.exp_name;

            if (expData) {
                this.plateView = expData.display_plate || expData.functional_team === "Microbial";
                // Fetch entity data
                if (!this.plateView) {
                    this.entityList = await fetchEntitySchemasByProtocolCode(this.protocolCode);
                }
            }
        },
        updateExperimentStartDate(startDateISOString) {
            this.experiment.day0 = startDateISOString
                ? toDateTimeForExperiment(startDateISOString, this.experiment)
                : null;
        },
        async getRequestsCount() {
            const requestsCount = await requestsAPI.fetchRequestCountByExperiment(
                this.experimentCode
            );
            this.requestsCount = requestsCount;
        },
        metadataUpdated(metadata) {
            const metadataFields = metadata.metadataFields;
            const day0 = metadataFields["Day0"] || null; // format: 2024-05-03
            const existingStartDate = this.experiment.day0;
            const existingStartDateISOString = existingStartDate
                ? existingStartDate.toISODate()
                : null;

            if (day0 !== existingStartDateISOString) {
                this.updateExperimentStartDate(day0);
            }
            this.experiment.isUsingMeasurements = metadata.invaioExperiment.isUsingMeasurements;
            this.experiment.experimentGroup = metadata.invaioExperiment.experimentGroup;
            this.experiment.status = metadata.invaioExperiment.status;
            this.experiment.qcStatus = metadata.invaioExperiment.qcStatus;
        },
        configureSamples(payload) {
            this.configureSamplesContext = payload;
            this.tabName = "Samples v2";
        },
        async createConfluencePage() {
            if (
                confirm(
                    `Are you sure you wish to create a Confluence notebook entry for ${this.experimentCode}? After doing so, you'll no longer be able to navigate to the Benchling notebook entry from this page.`
                )
            ) {
                await withLoadingScreen(async () => {
                    await confluenceAPI.createNotebookEntryForExperimentCode(this.experimentCode);
                });
                this.$router.go(0);
            }
        },
    },
});
</script>

<style scoped>
.action-header {
    min-height: 68px;
}
.expInfo {
    position: relative;
    font-weight: 500;
    float: left;
    max-width: 1200px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
</style>
